import { routes } from 'types/routes.ts'
import OverviewCard from '../OverviewCard.tsx'
import { useTranslation } from 'react-i18next'
import { useGetBillShockQuery } from 'store/queries/bolt-backend/billing-contracts'
import { BillShockOverviewCardProps } from './types.ts'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import { checkHasBudgetMeter, isTerminatedContractPastEndDate } from 'utils/contracts.ts'
import parse from 'html-react-parser'
import { Heading } from '@boltenergy-be/design-system'
import { EL_PRODUCT_CODES } from '@boltenergy-be/shared'
import dayjs from 'dayjs'
import { checkIsBillShock, checkIsWithinVisibleBillShockPeriod } from 'utils/billShock.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'

const BillShockOverviewCard = ({ accessRights, billingContract, email }: BillShockOverviewCardProps) => {
  // i18n
  const { t } = useTranslation('overview')

  // Constants
  const { electricity } = billingContract.serviceContracts
  const hasBudgetMeter = checkHasBudgetMeter(electricity)
  const isBoltFixed =
    electricity.detail.productCode === EL_PRODUCT_CODES.Fix || electricity.detail.productCode === EL_PRODUCT_CODES.PlentyFix
  const skipBillingCycles = !email || !billingContract || !accessRights[ACCESS_RIGHT_KEYS.billingCycles].showContent.current
  const terminatedContractPastEndDate = isTerminatedContractPastEndDate(billingContract.serviceContracts.electricity)

  // Redux queries
  const { data, isLoading, isError } = useGetBillShockQuery(
    { billingContractId: billingContract.contractNumber, email, serviceContracts: billingContract.serviceContracts },
    { skip: skipBillingCycles || terminatedContractPastEndDate }
  )

  // Extra constants
  const settlementEndDate = data?.billShock?.settlement?.endDate ? dayjs(data.billShock.settlement.endDate) : undefined
  const isWithinVisiblePeriod = checkIsWithinVisibleBillShockPeriod(data?.billShock?.settlement)
  const billShockType = checkIsBillShock(data?.billShock?.settlement?.billShockAmount, data?.billShock?.instalment?.current)
  const noBillShock = !data?.billShock || !billShockType || data?.billShock?.settlement?.billShockAmount <= 0 // Temporary hide negative bill shocks

  // Hide card if skipping billingCycles or if bill shock amount is negative
  if (
    isLoading ||
    skipBillingCycles ||
    noBillShock ||
    !!accessRights[ACCESS_RIGHT_KEYS.billingCycles].inaccuracyReason ||
    isError ||
    isBoltFixed ||
    hasBudgetMeter ||
    !isWithinVisiblePeriod
  ) {
    return null
  }

  return (
    <OverviewCard
      isLoading={isLoading}
      color="dark"
      button={{
        label: t(($) => $.billingCycles.button),
        href: routes.BILLING_INSTALMENT_CHECK,
        variant: 'primary'
      }}
    >
      {isLoading ? (
        <>
          <LoadingSkeleton.Title />
          <LoadingSkeleton.Paragraph lines={3} />
        </>
      ) : (
        <>
          <header>
            <OverviewCard.Title>{t(($) => $.billingCycles.title)}</OverviewCard.Title>
            <Heading as="h3" variant="h4" className="mt-100">
              {t(($) => $.billingCycles[billShockType].subtitle)}
            </Heading>
          </header>

          <p>
            {parse(
              t(($) => $.billingCycles[billShockType].description, {
                count: dayjs(settlementEndDate).diff(dayjs(), 'months') + 2
              })
            )}
          </p>
        </>
      )}
    </OverviewCard>
  )
}

export default BillShockOverviewCard
